import { computed } from 'vue';
import type { RouteLocationRaw } from 'vue-router';

import type { MenuItemCommandEvent } from 'primevue/menuitem';

import { useI18n } from 'vue-i18n';

import { adminUserMenuConfig, customerUserMenuConfig, type MenuItem } from '@/config/menu';
import { useAuthStore } from '@/stores/auth';

interface PrimeMenuItem {
  label?: string;
  icon?: string;
  to?: RouteLocationRaw;
  command?: (event: MenuItemCommandEvent) => void;
  items?: PrimeMenuItem[];
  separator?: boolean;
}

export function useUserMenu(onLogout: () => void) {
  const authStore = useAuthStore();
  const { t } = useI18n();

  const convertToPrimeMenu = (items: MenuItem[]): PrimeMenuItem[] => {
    return items.map(item => {
      const primeItem: PrimeMenuItem = {
        label: t(item.label),
        icon: item.icon,
      };

      if (item.to) {
        primeItem.to = item.to;
      }

      if (item.children && item.children.length > 0) {
        primeItem.items = convertToPrimeMenu(item.children);
      }

      return primeItem;
    });
  };

  const userMenuItems = computed(() => {
    const items: PrimeMenuItem[] = [];

    const baseMenuItems = authStore.currentContext === 'employee' ? adminUserMenuConfig : customerUserMenuConfig;

    const autoGeneratedItems = convertToPrimeMenu(baseMenuItems);

    const securityItems = autoGeneratedItems.filter(
      item =>
        item.label?.includes('Two-Factor') ||
        item.label?.includes('Password') ||
        item.label?.includes('Email') ||
        item.label?.includes('Login') ||
        item.label?.includes('Sessions') ||
        item.label?.includes('Security'),
    );

    const otherItems = autoGeneratedItems.filter(item => !securityItems.includes(item));

    items.push(...otherItems);

    if (securityItems.length > 0) {
      items.push(
        {
          label: t('menu.security') || 'Security',
          icon: 'pi pi-shield',
          items: securityItems,
        },
        { separator: true },
      );
    }

    items.push({
      label: t('menu.logout') || 'Logout',
      icon: 'pi pi-sign-out',
      command: onLogout,
    });

    return items;
  });

  return {
    userMenuItems,
  };
}
